import React, { useEffect, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { auth, signOut } from '../firebase';
import { getUserRole } from '../utils/getUserRole';
import { ensureUserDoc } from '../utils/ensureUserDoc';
import logo from '../assets/logo.png';
import AuthModal from './AuthModal';
import FilterButton from './FilterButton';
import SearchBar from './SearchBar';
import SearchSuggest from './SearchSuggest';

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

const Header = () => {
  const [user] = useAuthState(auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openAuth, setOpenAuth] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const [mobileSuggestOpen, setMobileSuggestOpen] = useState(false);

  const menuRef = useRef(null);
  const mobileSearchFormRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const navigate = useNavigate();
  const winW = useWindowWidth();
  const isNarrow = winW <= 900;

  const firstName = React.useMemo(() => {
    const name = user?.displayName?.trim();
    if (name) return name.split(/\s+/)[0];
    const email = user?.email || '';
    return email ? email.split('@')[0] : '';
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      localStorage.removeItem('userRole');
      setMenuOpen(false);
      navigate('/');
    }
  };

  const goToProfile = () => {
    navigate('/profile');
    setMenuOpen(false);
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    const q = mobileSearchValue.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSuggestOpen(false);
    setMobileSearchOpen(false);
    setMobileSearchValue('');
  };

  const handleLogoClick = async () => {
    if (!user) {
      navigate('/');
      return;
    }
    try {
      const role = await getUserRole(user.uid, user.email);
      localStorage.setItem('userRole', role || 'user');
      navigate(role === 'barista' ? '/barista/panel' : '/', { replace: true });
    } catch (e) {
      console.error("Не вдалося визначити роль користувача", e);
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    (async () => {
      if (user && !localStorage.getItem('userRole')) {
        try {
          const role = await getUserRole(user.uid, user.email);
          localStorage.setItem('userRole', role || 'user');
        } catch {
          localStorage.setItem('userRole', 'user');
        }
      }
    })();
  }, [user]);

  useEffect(() => {
    (async () => {
      if (user) await ensureUserDoc(user);
    })();
  }, [user]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (isNarrow && mobileSearchOpen) {
      mobileSearchInputRef.current?.focus();
    }
  }, [isNarrow, mobileSearchOpen]);

  useEffect(() => {
    if (!isNarrow) {
      setMobileSearchOpen(false);
      setMobileSuggestOpen(false);
      setMobileSearchValue('');
    }
  }, [isNarrow]);

  useEffect(() => {
    if (!isNarrow || !mobileSearchOpen) return;

    const onDocMouseDown = (e) => {
      const t = e.target;
      if (mobileSearchFormRef.current?.contains(t)) return;
      if (t?.closest?.('.mobile-search-suggest-panel')) return;

      setMobileSuggestOpen(false);
      setMobileSearchOpen(false);
      setMobileSearchValue('');
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isNarrow, mobileSearchOpen]);

  const mobileSearchOpenWidth = user ? 172 : 208;
  const headerHeight = isNarrow ? 56 : 64;

  return (
    <>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: isNarrow ? '8px 12px' : '10px 20px',
          backgroundColor: '#0b5226',
          color: '#fff',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: headerHeight,
          boxSizing: 'border-box',
        }}
      >
        <h1
          onClick={handleLogoClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isNarrow ? 8 : 12,
            fontSize: isNarrow ? 18 : 22,
            fontWeight: 700,
            cursor: 'pointer',
            margin: 0,
            color: '#fff',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          <img
            src={logo}
            alt="Книгообіг"
            draggable="false"
            style={{
              height: isNarrow ? 32 : 44,
              width: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.25))',
            }}
          />
          <span
            style={{
              display: 'inline-block',
              maxWidth: isNarrow ? (mobileSearchOpen ? 0 : 220) : 260,
              opacity: isNarrow && mobileSearchOpen ? 0 : 1,
              overflow: 'hidden',
              transition: 'max-width .22s ease, opacity .22s ease',
            }}
          >
            Книгообіг
          </span>
        </h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 1,
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <FilterButton
            showLabel={!(isNarrow && mobileSearchOpen)}
            style={{
              height: isNarrow ? 36 : 42,
              background: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.35)',
              color: '#fff',
            }}
          />

          {isNarrow && (
            <div
              style={{
                width: mobileSearchOpen ? mobileSearchOpenWidth : 36,
                maxWidth: mobileSearchOpen ? mobileSearchOpenWidth : 36,
                overflow: 'hidden',
                transition: 'max-width .25s ease, width .25s ease, transform .25s ease',
                transform: mobileSearchOpen ? 'translateX(-8px)' : 'translateX(0)',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {mobileSearchOpen ? (
                <form
                  ref={mobileSearchFormRef}
                  onSubmit={handleMobileSearchSubmit}
                  style={{
                    width: '100%',
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 999,
                    overflow: 'hidden',
                    background: '#fff',
                    boxShadow: '0 0 0 1px #cbd5e1 inset',
                  }}
                >
                  <input
                    ref={mobileSearchInputRef}
                    type="text"
                    value={mobileSearchValue}
                    onChange={(e) => {
                      setMobileSearchValue(e.target.value);
                      setMobileSuggestOpen(true);
                    }}
                    onFocus={() => setMobileSuggestOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setMobileSuggestOpen(false);
                        setMobileSearchOpen(false);
                        setMobileSearchValue('');
                      }
                    }}
                    placeholder="Знайти"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: 'none',
                      outline: 'none',
                      fontSize: 16,
                      padding: '0 10px',
                    }}
                  />
                <button
                    type="submit"
                    aria-label="Пошук"
                    style={{
                      height: '100%',
                      width: 38,
                      border: 'none',
                      background: '#16a34a',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => {
                    setMobileSearchOpen(true);
                    setMobileSuggestOpen(true);
                  }}
                  title="Відкрити пошук"
                  aria-label="Відкрити пошук"
                  style={{
                    height: 36,
                    width: 36,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,.35)',
                    background: 'rgba(255,255,255,.15)',
                    color: '#fff',
                    backdropFilter: 'blur(4px)',
                    cursor: 'pointer',
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                    <path d="M20 20L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {isNarrow && mobileSearchOpen && mobileSuggestOpen && (
            <SearchSuggest
              anchorRef={mobileSearchFormRef}
              search={mobileSearchValue}
              minPanelWidth={320}
              panelClassName="mobile-search-suggest-panel"
              onPick={(b) => {
                const id = b.isbn || b.ISBN || b.isbn13 || b.id;
                if (id) navigate(`/book/${encodeURIComponent(id)}`);
                setMobileSuggestOpen(false);
                setMobileSearchOpen(false);
                setMobileSearchValue('');
              }}
              onViewAll={(q) => {
                if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
                setMobileSuggestOpen(false);
                setMobileSearchOpen(false);
                setMobileSearchValue('');
              }}
              onClose={() => setMobileSuggestOpen(false)}
              maxItems={8}
            />
          )}

          {!isNarrow && (
            <SearchBar
              onPickBook={(b) => navigate(`/book/${encodeURIComponent(b.isbn || b.ISBN || b.isbn13 || b.id)}`)}
              onSearch={(q) => q && navigate(`/search?q=${encodeURIComponent(q)}`)}
              onViewAll={(q) => q && navigate(`/search?q=${encodeURIComponent(q)}`)}
            />
          )}
        </div>

        {user ? (
          <div style={{ position: 'relative' }} ref={menuRef}>
                <button
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                background: 'rgba(255,255,255,.15)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,.35)',
                color: '#fff',
                borderRadius: 999,
                padding: isNarrow ? 0 : '6px 10px',
                width: isNarrow ? 36 : 'auto',
                height: isNarrow ? 36 : 'auto',
                minWidth: isNarrow ? 36 : 'unset',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isNarrow ? 0 : 8,
                cursor: 'pointer',
                fontSize: isNarrow ? 15 : 16,
              }}
              title="Меню профілю"
            >
              <span
                style={{
                  color: '#fff',
                  fontWeight: 500,
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline',
                }}
              >
                {isNarrow ? ((firstName || user.email || 'U').charAt(0).toUpperCase()) : firstName}
              </span>
            </button>

            {menuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  borderRadius: 10,
                  marginTop: 8,
                  zIndex: 10,
                  minWidth: 180,
                }}
              >
                <button
                  onClick={goToProfile}
                  style={menuBtnStyle('#333', true)}
                >
                  {'\u041f\u0440\u043e\u0444\u0456\u043b\u044c'}
                </button>
                <button
                  onClick={() => {
                    navigate('/calc');
                    setMenuOpen(false);
                  }}
                  style={menuBtnStyle('#333', true)}
                >
                  Калькулятор вартості книги
                </button>
                <button
                  onClick={handleLogout}
                  style={menuBtnStyle('#d00', false)}
                >
                  Вийти
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setOpenAuth(true)}
            style={{
              background: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.35)',
              color: '#fff',
              borderRadius: 999,
              padding: '8px 14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Увійти
          </button>
        )}
      </header>
      <div aria-hidden style={{ height: headerHeight }} />

      {openAuth && (
        <AuthModal
          open={openAuth}
          onClose={() => setOpenAuth(false)}
        />
      )}
    </>
  );
};

function menuBtnStyle(color, withBorder) {
  return {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    fontSize: 18,
    lineHeight: 1.25,
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    color,
    borderBottom: withBorder ? '1px solid #eee' : 'none',
  };
}

export default Header;





